import React, { useCallback, useRef } from "react";
import { useFormikContext } from "formik";
import {
  FieldLabel,
  TextInput,
  FieldHelperText,
  PlainTextField,
  Link,
} from "@td/ui";
import { translate } from "@td/shared_utils";
import { useMemberSearch } from "../hooks";
import { fieldTypes, defaultTaskFormValues } from "../constants";

const MemberLink = React.memo(({ memberId, ...props }) => (
  <Link
    {...props}
    target="_blank"
    href={window.Routes.member_path(memberId)}
  />
));

const MemberField = React.memo(() => {
  const textInputRef = useRef();
  const { findMember } = useMemberSearch();
  const { values, getFieldMeta, setFieldValue, setFieldError, setFieldTouched } = useFormikContext();
  const fieldMeta = getFieldMeta(fieldTypes.MEMBER);

  const member = values[fieldTypes.MEMBER].value || {};
  const memberId = member.id || null;
  const isRestricted = member.isRestrictedForAdmin || false;
  const isReadOnly = !values[fieldTypes.NOT_CONSULT_RELATED];

  const clearFields = useCallback(() => {
    setFieldValue(fieldTypes.MEMBER, defaultTaskFormValues[fieldTypes.MEMBER], false);
    setFieldValue(fieldTypes.GROUP, defaultTaskFormValues[fieldTypes.GROUP], false);
    setFieldValue(fieldTypes.PROVIDER, defaultTaskFormValues[fieldTypes.PROVIDER], false);
  }, [setFieldValue]);

  const handleKeyDown = useCallback(async (e) => {
    setFieldTouched(fieldTypes.MEMBER, true, false);

    if (e.key === "Enter") {
      e.preventDefault();

      if (e.target.value) {
        const member = await findMember(e.target.value);

        if (member) {
          setFieldValue(`${fieldTypes.MEMBER}.value`, member, false);
          setFieldValue(fieldTypes.GROUP, member.group.name, false);

          textInputRef.current.blur();
        } else {
          clearFields();
          setFieldError(
            fieldTypes.MEMBER,
            translate(null, "care_team_tasks.form.fields.member.errors", "not_found")
          );
        }
      } else {
        clearFields();
        setFieldError(
          fieldTypes.MEMBER,
          translate(null, "care_team_tasks.form.fields.member.errors", "required")
        );
      }
    }
  }, [textInputRef.current, clearFields, setFieldValue, setFieldError, setFieldTouched]);

  const handleChange = useCallback((e) => {
    if (/\D/.test(e.target.value)) {
      setFieldValue(`${fieldTypes.MEMBER}.label`, e.target.value.replace(/\D/g, ""), false);
      setFieldError(
        fieldTypes.MEMBER,
        translate(null, "care_team_tasks.form.fields.member.errors", "only_digits")
      );
    } else {
      setFieldValue(`${fieldTypes.MEMBER}.label`, e.target.value, false);
      setFieldError(fieldTypes.MEMBER, null);
    }
  }, [setFieldValue, setFieldError]);

  const handleFocus = useCallback(() => {
    const { value } = values[fieldTypes.MEMBER];

    setFieldValue(`${fieldTypes.MEMBER}.label`, value && value.id || "", false);
  }, [values[fieldTypes.MEMBER]]);

  const handleBlur = useCallback(() => {
    const { value } = values[fieldTypes.MEMBER];

    if (value) {
      setFieldValue(
        `${fieldTypes.MEMBER}.label`,
        translate(null, "care_team_tasks.form.fields.member.option", "label", {
          id: value.id,
          name: value.name,
        }),
        false
      );
    } else {
      setFieldValue(`${fieldTypes.MEMBER}.label`, "", false);
    }
  }, [values[fieldTypes.MEMBER]]);

  return (
    <React.Fragment>
      {!isReadOnly ? (
        <FieldLabel>
          {memberId ? (
            <MemberLink
              memberId={memberId}
              restricted={isRestricted}
            >
              {translate(null, "care_team_tasks.form.fields.member", "label")}*
            </MemberLink>
          ) : (
            `${translate(null, "care_team_tasks.form.fields.member", "label")}*`
          )}

          <TextInput
            ref={textInputRef}
            autoComplete="off"
            value={values[fieldTypes.MEMBER].label || ""}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={translate(null, "care_team_tasks.form.fields.member", "placeholder")}
            invalid={fieldMeta.error && fieldMeta.touched}
          />

          <FieldHelperText error={fieldMeta.error && fieldMeta.touched}>
            {fieldMeta.error}
          </FieldHelperText>
        </FieldLabel>
      ) : (
        <React.Fragment>
          {member && (
            <MemberLink
              memberId={memberId}
              restricted={isRestricted}
            >
              <PlainTextField
                name={`${fieldTypes.MEMBER}.label`}
                label={`${translate(null, "care_team_tasks.form.fields.member", "label")}:`}
              />
            </MemberLink>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
});

export default MemberField;
